import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import { Spin, Input, Select } from "antd";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { toast } from "react-toastify";
import {
  getAllAttendance,
  postAttendanceSignIn,
  putAttendanceSignOut,
  AttendanceRecord,
} from "../../../core/api/useAttendance";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import dayjs from "dayjs";
import ReactDatePicker from "react-datepicker";
import { fetchEmployeeImage } from "../../../core/api/fetchEmployeeImage";

const { Search } = Input;
const { Option } = Select;

const AttendanceEmployee = () => {
  // Get employeeId from Redux state - handle both string and number types
  const employeeIdFromState = useSelector(
    (state: RootState) => state.auth.employeeId
  );

  // Don't convert to number if it's already a string ID like "EPM0026"
  // Only convert to number if it's a valid numeric string
  const employeeId = (() => {
    if (!employeeIdFromState || employeeIdFromState === "NaN") return null;

    // If it's a string that starts with letters (like EPM0026), keep it as string
    if (
      typeof employeeIdFromState === "string" &&
      /^[A-Za-z]/.test(employeeIdFromState)
    ) {
      return employeeIdFromState;
    }

    // If it's a numeric string, convert to number
    const numericValue = Number(employeeIdFromState);
    return !isNaN(numericValue) ? numericValue : employeeIdFromState;
  })();

  const name = useSelector((state: RootState) => state.auth.name);
  const empToken = useSelector((state: any) => state.auth.token);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [signedIn, setSignedIn] = useState(false);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [filteredData, setFilteredData] = useState<AttendanceRecord[]>([]);
  const [weekHours, setWeekHours] = useState<number>(0);
  const [monthHours, setMonthHours] = useState<number>(0);
  const [monthOvertime, setMonthOvertime] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [todayRecord, setTodayRecord] = useState<any>(null);
  const [isPunchDevice, setIsPunchDevice] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState<"date" | "month">("month");
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [employeeImage, setEmployeeImage] = useState<string | null>(null);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const toggleMonthDropdown = () => setIsMonthDropdownOpen((prev) => !prev);

  const handleMonthClick = (idx: number) => {
    setSelectedMonth(idx);
    setIsMonthDropdownOpen(false);
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadAttendance();
  }, []);

  useEffect(() => {
    filterData();
  }, [attendanceData, searchText]);

  // Fetch employee image when employeeId or empToken changes
  useEffect(() => {
    if (employeeId && empToken) {
      fetchEmployeeImage(employeeId.toString(), empToken).then((imageUrl) => {
        setEmployeeImage(imageUrl);
      });
    }
  }, [employeeId, empToken]);

  const filterData = () => {
    let filtered = [...attendanceData];
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Search filter
    if (searchText) {
      filtered = filtered.filter(
        (record) =>
          record.status?.toLowerCase().includes(searchText.toLowerCase()) ||
          record.source?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  const loadAttendance = async () => {
    try {
      setLoading(true);
      const records = await getAllAttendance(empToken);
      // Check what fields are available in the first record
      if (records.length > 0) {
      }

      // Filter records for current employee - Fixed type-safe comparisons
      const filtered = records.filter((r) => {
        // Skip if no valid employeeId
        if (!employeeId || employeeId === "NaN") return false;

        // Convert all values to strings for safe comparison
        const recordIdlStr = String(r.idl || "");
        const recordIdStr = String(r.id || "");
        const recordEmployeeIdStr = String(r.employee_id || "");
        const employeeIdStr = String(employeeId || "");
        const employeeIdFromStateStr = String(employeeIdFromState || "");

        // Primary comparison: match employee_id field (most likely to match "EPM0026")
        if (recordEmployeeIdStr === employeeIdStr) return true;
        if (
          employeeIdFromState &&
          recordEmployeeIdStr === employeeIdFromStateStr
        )
          return true;

        // Secondary comparisons for other ID fields
        if (recordIdlStr === employeeIdStr || recordIdStr === employeeIdStr)
          return true;
        if (
          employeeIdFromState &&
          (recordIdlStr === employeeIdFromStateStr ||
            recordIdStr === employeeIdFromStateStr)
        )
          return true;

        // Numeric comparisons only if employeeId is a number
        if (typeof employeeId === "number" && !isNaN(employeeId)) {
          if (r.idl === employeeId || r.id === employeeId) return true;
          if (
            !isNaN(Number(r.employee_id)) &&
            Number(r.employee_id) === employeeId
          )
            return true;
        }

        return false;
      });
      setAttendanceData(filtered || []);

      // Check if user is currently signed in by looking at today's record
      if (filtered?.length > 0) {
        const today = new Date().toISOString().split("T")[0];
        const todayRec = filtered.find((record) => {
          const recordDate = new Date(
            record.check_in || record.punch_in || record.date || ""
          )
            .toISOString()
            .split("T")[0];
          return recordDate === today;
        });

        if (todayRec) {
          setTodayRecord(todayRec);

          // Check if it's a punch device entry
          const isPunch = Boolean(todayRec.punch_in || todayRec.punch_out);
          setIsPunchDevice(isPunch);

          // User is signed in if they have check_in/punch_in but no check_out/punch_out
          const hasSignedIn = Boolean(todayRec.check_in || todayRec.punch_in);
          const hasSignedOut = Boolean(
            todayRec.check_out || todayRec.punch_out
          );
          setSignedIn(hasSignedIn && !hasSignedOut);
        } else {
          setTodayRecord(null);
          setIsPunchDevice(false);
          setSignedIn(false);
        }
      } else {
        setTodayRecord(null);
        setIsPunchDevice(false);
        setSignedIn(false);
      }
    } catch (err: any) {
      console.error("Failed to load attendance", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    // Check if employeeId is valid
    if (!employeeId || employeeId === "NaN") {
      console.error("Invalid Employee ID:", {
        employeeId,
        employeeIdFromState,
      });
      return toast.warning("Employee ID not available. Please login again.");
    }

    // Check if user checked in using punch device
    if (
      todayRecord &&
      isPunchDevice &&
      todayRecord.punch_in &&
      !todayRecord.punch_out
    ) {
      return toast.warning(
        "You checked in today using device, so you're not capable of online check-in."
      );
    }

    try {
      await postAttendanceSignIn(empToken);
      setSignedIn(true);
      loadAttendance();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail === "Already signed in for the day.") {
        toast.warning(
          "You are already signed in for today. If you used a punch device, please use check-out to sign out."
        );
      } else {
        console.error("Check-in error:", err);
      }
    }
  };

  const handleCheckOut = async () => {
    // Check if employeeId is valid
    if (!employeeId || employeeId === "NaN") {
      console.error("Invalid Employee ID:", {
        employeeId,
        employeeIdFromState,
      });
      return toast.warning("Employee ID not available. Please login again.");
    }

    // Check if user checked in using punch device and hasn't checked out yet
    if (
      todayRecord &&
      isPunchDevice &&
      todayRecord.punch_in &&
      !todayRecord.punch_out
    ) {
      return toast.warning(
        "You checked in today using device, so you're not capable of online check-out."
      );
    }

    try {
      await putAttendanceSignOut(empToken);
      setSignedIn(false);
      loadAttendance();
    } catch (err: any) {
      console.error("Check-out error:", err);
    }
  };

  const formatTime = (time: string | null) => {
    if (!time) return "-";
    try {
      // Handle both full datetime and time-only strings
      let date;
      if (time.includes("T")) {
        // Full datetime string
        date = new Date(time);
      } else {
        // Time only string (HH:MM:SS)
        date = new Date(`1970-01-01T${time}Z`);
      }

      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "-";
    }
  };

  // Format duration (ms) to HH:MM:SS
  const formatDuration = (duration: number): string => {
    const hrs = Math.floor(duration / 3600000);
    const mins = Math.floor((duration % 3600000) / 60000);
    const secs = Math.floor((duration % 60000) / 1000);
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  // Get live working time only if status ≠ Absent
  const getLiveWorkingTime = (
    checkIn: string | null,
    status: string
  ): string => {
    if (!checkIn || status === "Absent") return "-";
    const start = new Date(checkIn).getTime();
    const now = Date.now();
    const diff = now - start;
    return formatDuration(diff);
  };

  // Example: Customize according to your org’s week-off policy
  const isWeekOff = (date: Date): boolean => {
    const day = date.getDay(); // 0=Sun, 6=Sat
    return day === 0; // Sunday as week-off
  };

  const getColumns = (): any[] => [
    {
      title: "Date",
      key: "date",
      width: 100,
      align: "center" as const,
      render: (record: any) => {
        const dateValue =
          record.date ||
          record.check_in ||
          record.punch_in ||
          record.created_date ||
          record.attendance_date;

        if (!dateValue) return "-";

        const dateObj = new Date(dateValue);

        // Format date: dd/MM/yyyy
        const formattedDate = dateObj.toLocaleDateString("en-GB");

        // Get short day (Mon, Tue, etc.)
        const dayShort = dateObj
          .toLocaleDateString("en-US", { weekday: "short" }) // e.g. "Mon"
          .slice(0, 3); // ensure 3-letter format

        // Determine badge color logic (example from your reference)
        const badgeColor = isWeekOff(dateObj)
          ? "#fd7e14" // Week Off
          : dayShort === "Sat"
          ? "#fd0d91ff" // Saturday
          : "#1763AD"; // Default

        return (
          <div className="text-center d-flex flex-column align-items-center gap-1">
            <div className="text-dark">
              <b>{formattedDate}</b>
            </div>
            <span
              className="badge"
              style={{
                backgroundColor: badgeColor,
                color: "#fff",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
              }}
            >
              {dayShort}
            </span>
          </div>
        );
      },
    },

    {
      title: "Check In & Out",
      key: "check",
      width: 130,
      align: "center" as const,
      render: (record: any) => {
        const isPunch = Boolean(record.punch_in || record.punch_out);
        const checkIn = isPunch ? record.punch_in : record.check_in;
        const checkOut = isPunch ? record.punch_out : record.check_out;

        const getBadge = (iconClass: string, timeValue: string | null) => (
          <div
            className={`badge ${
              iconClass.includes("logout")
                ? "badge bg-danger-subtle text-danger"
                : "badge bg-success-subtle text-success"
            }`}
            style={{
              fontSize: "10px",
              padding: "6px 10px",
              width: "100px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className={`ti ${iconClass}`} style={{ fontSize: "12px" }} />
            <span style={{ fontSize: "11px", fontWeight: 500 }}>
              {timeValue ? formatTime(timeValue) : "Pending"}
            </span>
          </div>
        );

        return (
          <div className="d-flex flex-column align-items-center gap-1">
            {getBadge(isPunch ? "ti-fingerprint" : "ti-device-laptop", checkIn)}
            {getBadge("ti-logout", checkOut)}
          </div>
        );
      },
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      width: 110,
      align: "center" as const,
      render: (_: any, record: any) => {
        const isPunch = Boolean(record.punch_in || record.punch_out);
        const checkIn = isPunch ? record.punch_in : record.check_in;
        const checkOut = isPunch ? record.punch_out : record.check_out;
        const status = record.status;

        const hasCheckedOut = Boolean(checkOut);
        const storedHours = record.working_hours;

        const displayTime =
          status === "Absent"
            ? "-"
            : hasCheckedOut
            ? storedHours || "-"
            : checkIn
            ? getLiveWorkingTime(checkIn, status)
            : "-";

        return (
          <span className="text-dark">
            <b>{displayTime}</b>
          </span>
        );
      },
    },
    {
      title: "Production Hours",
      dataIndex: "production_hr",
      width: 100,
      align: "center" as const,
      render: (hours: string) => (
        <span className="text-dark">{hours || "-"}</span>
      ),
    },
    {
      title: "Overtime",
      dataIndex: "over_time",
      width: 90,
      align: "center" as const,
      render: (overtime: string) => (
        <span className="text-dark">{overtime || "-"}</span>
      ),
    },
    {
      title: "Break",
      dataIndex: "Break",
      width: 80,
      align: "center" as const,
      render: () => (
        <span className="d-inline-flex align-items-center">
          <span
            className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
            style={{
              width: "20px",
              height: "20px",
              minWidth: "20px",
              minHeight: "20px",
              maxWidth: "20px",
              maxHeight: "20px",
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <i className="ti ti-clock-pause" style={{ fontSize: "12px" }} />
          </span>
          1 hr
        </span>
      ),
    },
    {
      title: "Late/Early",
      key: "late_early",
      width: 90,
      align: "center" as const,
      render: (_: any, record: any) => (
        <div>
          <div
            style={{ fontSize: "11px", color: "#dc3545", fontWeight: "500" }}
          >
            {record.late || record.LateComing || "--"}
          </div>
          <div
            style={{ fontSize: "11px", color: "#28a745", fontWeight: "500" }}
          >
            {record.earlyout || record.EarlyGoing || "--"}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      align: "center" as const,
      render: (status: string) => {
        let className = "badge bg-light-transparent";
        if (status === "Present") className = "badge bg-success-transparent";
        else if (status?.includes("Half"))
          className = "badge bg-warning-transparent";
        else if (status === "Absent") className = "badge bg-danger-transparent";
        else if (status === "Late") className = "badge bg-pink-transparent";
        else if (status === "Early") className = "badge bg-purple-transparent";

        return (
          <span
            className={className}
            style={{
              fontSize: "12px",
              fontWeight: "500",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            {status || "-"}
          </span>
        );
      },
    },
    {
      title: "Source",
      dataIndex: "source",
      width: 80,
      align: "center" as const,
      render: (source: string) => (
        <span className="text-dark">{source || "-"}</span>
      ),
    },
  ];

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    if (hour < 20) return "Good Evening";
    return "Good Night";
  };

  // Show error message if employeeId is not available
  if (!employeeId || employeeId === "NaN") {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">
            <h5>Error: Employee ID not found</h5>
            <p>Please login again to access the attendance page.</p>
            <p>
              Debug Info: employeeId = {String(employeeId)}, state ={" "}
              {String(employeeIdFromState)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Self Attendance</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.employeeDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Attendance</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "month" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => {
                  setViewMode("month");
                  setSelectedDate(null);
                }}
              >
                Month
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "date" ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setViewMode("date")}
              >
                Date
              </button>
            </div>
            {viewMode === "date" ? (
              <ReactDatePicker
                selected={selectedDate ? selectedDate.toDate() : null}
                onChange={(date) => setSelectedDate(dayjs(date))}
                customInput={
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center"
                    style={{ maxHeight: "34px", minWidth: "130px" }}
                  >
                    <i className="ti ti-calendar me-2" />
                    {selectedDate
                      ? selectedDate.format("DD-MM-YYYY")
                      : "Select Date"}
                  </button>
                }
                dateFormat="dd-MM-yyyy"
                popperPlacement="bottom-start"
              />
            ) : (
              <div className="position-relative" style={{ width: "160px" }}>
                <button
                  onClick={toggleMonthDropdown}
                  className="btn btn-outline-primary d-flex align-items-center justify-content-between w-100"
                  style={{ maxHeight: "34px" }}
                >
                  <i className="ti ti-calendar-month me-2" />
                  {monthNames[selectedMonth]} - {selectedYear}
                  <i className="ti ti-chevron-down ms-auto" />
                </button>
                {isMonthDropdownOpen && (
                  <div
                    className="dropdown-menu show p-3 shadow position-absolute bg-white"
                    style={{
                      top: "42px",
                      minWidth: "280px",
                      zIndex: 1000,
                      borderRadius: "8px",
                    }}
                  >
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Select Month:
                      </label>
                      <div className="d-flex flex-wrap gap-1">
                        {monthNames.map((month, idx) => (
                          <button
                            key={month}
                            onClick={() => handleMonthClick(idx)}
                            className={`btn btn-sm ${
                              selectedMonth === idx
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            style={{ fontSize: "11px", padding: "4px 8px" }}
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="form-label fw-semibold">Year:</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(+e.target.value)}
                        min="2020"
                        max="2030"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <CollapseHeader />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-4 d-flex">
            <div className="card flex-fill border-primary attendance-bg">
              <div className="card-body">
                <div className="mb-4 text-center">
                  <h6 className="fw-medium text-gray-5 mb-2">
                    {getGreeting()}, {name}
                  </h6>
                  <h4 className="mb-3">
                    {currentTime.toLocaleTimeString()} –{" "}
                    {currentTime.toDateString()}
                  </h4>
                  <div className="small text-muted">
                    Employee ID: {employeeId}
                  </div>
                  <div className="avatar avatar-xxl avatar-rounded my-3">
                    <img
                      src={employeeImage || "/assets/img/defaultavatar.png"} // Use dynamic image or fallback
                      alt="/assets/img/defaultavatar.png"
                    />
                  </div>
                  <h6 className="fw-medium mb-3">
                    <i className="ti ti-fingerprint text-primary me-2" />
                    {signedIn ? "Checked In" : "Not Checked In"}
                  </h6>
                  {isPunchDevice && todayRecord && (
                    <div
                      className="alert alert-info p-2 mb-3"
                      style={{ fontSize: "12px" }}
                    >
                      <i className="ti ti-info-circle me-1" />
                      You used punch device today
                    </div>
                  )}
                  {!signedIn ? (
                    <button
                      onClick={handleCheckIn}
                      className="btn btn-success w-100"
                      disabled={isPunchDevice && todayRecord}
                    >
                      Check In
                    </button>
                  ) : (
                    <button
                      onClick={handleCheckOut}
                      className="btn btn-danger w-100"
                      disabled={isPunchDevice && todayRecord}
                    >
                      Check Out
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-8 d-flex">
            <div className="row flex-fill">
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-2">
                      <span className="avatar avatar-sm bg-primary mb-2">
                        <i className="ti ti-clock-stop" />
                      </span>
                      <h2 className="mb-2">
                        0 / <span className="fs-20 text-gray-5"> 0</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Total Hours Today
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-2">
                      <span className="avatar avatar-sm bg-dark mb-2">
                        <i className="ti ti-clock-up" />
                      </span>
                      <h2 className="mb-2">
                        0 / <span className="fs-20 text-gray-5"> 0</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Total Hours Week
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-2">
                      <span className="avatar avatar-sm bg-info mb-2">
                        <i className="ti ti-calendar-up" />
                      </span>
                      <h2 className="mb-2">
                        0 / <span className="fs-20 text-gray-5"> 0</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Total Hours Month
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-md-6">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-2">
                      <span className="avatar avatar-sm bg-pink mb-2">
                        <i className="ti ti-calendar-star" />
                      </span>
                      <h2 className="mb-2">
                        0 / <span className="fs-20 text-gray-5"> 0</span>
                      </h2>
                      <p className="fw-medium text-truncate">
                        Overtime this Month
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-xl-3">
                        <div className="mb-4">
                          <p className="d-flex align-items-center mb-1">
                            <i className="ti ti-point-filled text-dark-transparent me-1" />
                            Total Working hours
                          </p>
                          <h3>0</h3>
                        </div>
                      </div>
                      <div className="col-xl-3">
                        <div className="mb-4">
                          <p className="d-flex align-items-center mb-1">
                            <i className="ti ti-point-filled text-success me-1" />
                            Productive Hours
                          </p>
                          <h3>0</h3>
                        </div>
                      </div>
                      <div className="col-xl-3">
                        <div className="mb-4">
                          <p className="d-flex align-items-center mb-1">
                            <i className="ti ti-point-filled text-warning me-1" />
                            Break hours
                          </p>
                          <h3>0</h3>
                        </div>
                      </div>
                      <div className="col-xl-3">
                        <div className="mb-4">
                          <p className="d-flex align-items-center mb-1">
                            <i className="ti ti-point-filled text-info me-1" />
                            Overtime
                          </p>
                          <h3>0</h3>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div
                          className="progress bg-transparent-dark mb-3"
                          style={{ height: 24 }}
                        >
                          <div
                            className="progress-bar bg-white rounded"
                            role="progressbar"
                            style={{ width: "18%" }}
                          />
                          <div
                            className="progress-bar bg-success rounded me-2"
                            role="progressbar"
                            style={{ width: "18%" }}
                          />
                          <div
                            className="progress-bar bg-warning rounded me-2"
                            role="progressbar"
                            style={{ width: "5%" }}
                          />
                          <div
                            className="progress-bar bg-success rounded me-2"
                            role="progressbar"
                            style={{ width: "28%" }}
                          />
                          <div
                            className="progress-bar bg-warning rounded me-2"
                            role="progressbar"
                            style={{ width: "17%" }}
                          />
                          <div
                            className="progress-bar bg-success rounded me-2"
                            role="progressbar"
                            style={{ width: "22%" }}
                          />
                          <div
                            className="progress-bar bg-warning rounded me-2"
                            role="progressbar"
                            style={{ width: "5%" }}
                          />
                          <div
                            className="progress-bar bg-info rounded me-2"
                            role="progressbar"
                            style={{ width: "3%" }}
                          />
                          <div
                            className="progress-bar bg-info rounded"
                            role="progressbar"
                            style={{ width: "2%" }}
                          />
                          <div
                            className="progress-bar bg-white rounded"
                            role="progressbar"
                            style={{ width: "18%" }}
                          />
                        </div>
                      </div>
                      <div className="co-md-12">
                        <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                          <span className="fs-10">06:00</span>
                          <span className="fs-10">07:00</span>
                          <span className="fs-10">08:00</span>
                          <span className="fs-10">09:00</span>
                          <span className="fs-10">10:00</span>
                          <span className="fs-10">11:00</span>
                          <span className="fs-10">12:00</span>
                          <span className="fs-10">01:00</span>
                          <span className="fs-10">02:00</span>
                          <span className="fs-10">03:00</span>
                          <span className="fs-10">04:00</span>
                          <span className="fs-10">05:00</span>
                          <span className="fs-10">06:00</span>
                          <span className="fs-10">07:00</span>
                          <span className="fs-10">08:00</span>
                          <span className="fs-10">09:00</span>
                          <span className="fs-10">10:00</span>
                          <span className="fs-10">11:00</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h5 className="mb-0">My Attendance Records</h5>
              </div>
              <div className="col-md-6">
                <div className="d-flex justify-content-end gap-2">
                  <Search
                    placeholder="Search records..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 200 }}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <Spin spinning={loading}>
              <Table
                dataSource={filteredData}
                columns={getColumns()}
                rowKey="id"
                scroll={{ x: 1200 }}
                size="small"
                className="table-responsive"
              />
            </Spin>
          </div>
        </div>
      </div>
    </div>
  );
};
export default AttendanceEmployee;
